import React from "react";

import { faSparkles, faXmark } from "@fortawesome/pro-solid-svg-icons";

import { ButtonIcon, Icon } from "@performyard/py-components";

import { SuggestedActions } from "./SuggestedActions";

type PopupCardProps = {
	title: string;
	action?: {
		label: string;
		onClick: () => void;
	};
	onDismiss: () => void;
	/**
	 * Whether the card casts its own shadow. A caller that draws a caret against the card sets this
	 * false and puts the shadow on the wrapper instead, so caret and card cast one shadow and read as
	 * a single shape — a shadow on the card alone would darken the caret's base into a divider.
	 */
	withShadow?: boolean;
};

/**
 * The Ask PerformYard callout card: sparkles, title, optional action, dismiss x.
 *
 * Node 50:4823 — 280px wide, 12px padding, 8px between the three columns, and a shadow instead of a
 * border, so the dismiss x is a flex sibling of the text rather than absolutely placed over it.
 *
 * Presentational only. Whether a callout is shown, where it sits, and what dismissing it persists
 * all belong to whoever renders this — `DismissiblePopup` for the callouts above the floating
 * trigger, `FormAuthoringCoachPopup` for the one anchored under the top-nav trigger, which needs its
 * own positioning, caret, and measurement of the box it hangs into.
 */
export const PopupCard: React.FC<PopupCardProps> = ({ title, action, onDismiss, withShadow = true }) => (
	<div
		className={`relative flex items-start gap-2 rounded-md bg-neutral-0 p-3 ${
			withShadow ? "shadow-[0_4px_30px_0_rgba(0,0,0,0.15)]" : ""
		}`}
	>
		{/* Both 16px gutters hold their size against a long title, which wraps in the column between them. */}
		<Icon icon={faSparkles} size={16} color="primary-5" dangerouslySetClassName="shrink-0" />
		<div className="flex min-w-0 flex-1 flex-col items-start gap-1">
			<span className="text-paragraph-3 font-semibold text-neutral-9">{title}</span>
			{action && <SuggestedActions actions={[{ label: action.label, onClick: action.onClick }]} />}
		</div>
		<ButtonIcon
			icon={faXmark}
			onClick={onDismiss}
			aria-label="Dismiss"
			variant="standard"
			color="neutral"
			shade={5}
			size={16}
			dangerouslySetClassName="shrink-0"
		/>
	</div>
);

export default PopupCard;
